import React, { useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import StarterKit from '@tiptap/starter-kit'
import Heading from '@tiptap/extension-heading'
import Link from '@tiptap/extension-link'
import Highlight from '@tiptap/extension-highlight'
import { Button } from './button'
import { cn } from '@/lib/utils'
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  List as ListIcon,
  ListOrdered as ListOrderedIcon,
  Heading1 as Heading1Icon,
  Heading2 as Heading2Icon,
  Heading3Icon,
  Heading4Icon,
  Link as LinkIcon,
  Unlink as UnlinkIcon,
} from 'lucide-react'
import './rich-text-editor.css'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './dialog'
import { Input } from './input'

interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  className?: string
  autoFocus?: boolean
  headerRight?: React.ReactNode
  onSourceClick?: (sourceIndex: number) => void
  disabled?: boolean
  hideButtons?: boolean
  onFocus?: () => void
  onBlur?: () => void
  forceBulletList?: boolean // When true, always enforce bullet list format
  isEditing?: boolean // When true, skip prop-based content updates to preserve cursor position
}

export function RichTextEditor({
  value,
  onChange,
  className,
  autoFocus = false,
  headerRight,
  onSourceClick,
  disabled = false,
  hideButtons = false,
  onFocus,
  onBlur,
  forceBulletList = false,
  isEditing = false,
}: RichTextEditorProps) {
  const [showLinkDialog, setShowLinkDialog] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: false,
          link: false,
        }),
        //   TextStyle,
        //   Underline,
        Heading.configure({
          levels: [1, 2, 3, 4],
        }),
        Link.configure({
          openOnClick: true,
          autolink: true,
        }),
        Highlight.configure({
          multicolor: true,
        }),
      ],
      content: value,
      editable: !disabled,
      editorProps: {
        attributes: {
          class: 'focus:outline-none min-h-[120px] p-3',
        },
        handleClick: (_view, _pos, event) => {
          const target = event.target as HTMLElement
          if (target && target.classList.contains('source-reference-btn') && onSourceClick) {
            event.preventDefault()
            const sourceIndex = target.getAttribute('data-source-index')
            if (sourceIndex) {
              onSourceClick(parseInt(sourceIndex, 10))
            }
            return true // Prevent default handling
          }
          return false // Allow default handling
        },
        handleKeyDown: (view, event) => {
          // If forceBulletList mode, prevent exiting the bullet list
          if (forceBulletList) {
            const { state } = view
            const { $from } = state.selection

            // Walk up the node tree to check if we're in a bullet list
            let depth = $from.depth
            let inBulletList = false

            while (depth > 0) {
              const node = $from.node(depth)
              if (node.type.name === 'bulletList') {
                inBulletList = true
                break
              }
              depth--
            }

            // If typing outside a bullet list, prevent it and force back into list
            if (!inBulletList) {
              // Any printable character or Enter - force into bullet list
              if (event.key.length === 1 || event.key === 'Enter') {
                event.preventDefault()

                // Save the character to insert after toggling bullet list
                const charToInsert = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey ? event.key : null

                // Toggle bullet list first
                editor?.chain().focus().toggleBulletList().run()

                // Then insert the character if needed
                if (charToInsert) {
                  // Use setTimeout to ensure bullet list is active first
                  setTimeout(() => {
                    editor?.commands.insertContent(charToInsert)
                  }, 0)
                }
                return true
              }
            }

            // Handle Enter key in bullet list
            if (event.key === 'Enter' && !event.shiftKey && inBulletList) {
              // Check if current list item is empty
              let listItemDepth = $from.depth
              while (listItemDepth > 0) {
                const node = $from.node(listItemDepth)
                if (node.type.name === 'listItem') {
                  // Check if this list item is empty
                  if (node.textContent.trim() === '') {
                    // Empty list item - prevent exiting the list
                    event.preventDefault()
                    return true
                  }
                  break
                }
                listItemDepth--
              }
            }
          }
          return false
        },
      },
      onUpdate: ({ editor }) => {
        onChange(editor.getHTML())
      },
      onFocus: () => {
        onFocus?.()
      },
      onBlur: () => {
        onBlur?.()
      },
    },
    [onSourceClick, forceBulletList]
  )

  React.useEffect(() => {
    if (editor && !editor.isDestroyed) {
      // Skip content updates while user is actively editing to preserve cursor position
      if (isEditing) {
        return
      }

      const currentContent = editor.getHTML()
      // Only update if content is actually different
      // Normalize whitespace for comparison to avoid false positives
      const normalizeHtml = (html: string) => html.replace(/>\s+</g, '><').trim()
      if (normalizeHtml(value) !== normalizeHtml(currentContent)) {
        console.debug('[RichTextEditor] Setting content from prop', {
          valueLength: value.length,
          currentLength: currentContent.length,
          areDifferent: true
        })
        editor.commands.setContent(value, { emitUpdate: false }) // Don't trigger onUpdate
      }
    }
  }, [editor, value, isEditing])

  React.useEffect(() => {
    if (editor && autoFocus) {
      editor.commands.focus()
    }
  }, [editor, autoFocus])

  if (!editor) {
    return null
  }

  const handleOpenLinkDialog = () => {
    const currentLink = editor.getAttributes('link').href
    setLinkUrl(currentLink || '')
    setShowLinkDialog(true)
  }

  const handleAddLink = () => {
    if (!linkUrl.trim()) {
      handleRemoveLink()
      return
    }

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: linkUrl })
      .run()

    setShowLinkDialog(false)
    setLinkUrl('')
  }

  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run()
    setShowLinkDialog(false)
    setLinkUrl('')
  }

  const handleKeyDownInLinkDialog = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddLink()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setShowLinkDialog(false)
      setLinkUrl('')
    }
  }

  const handleWrapperClick = (e: React.MouseEvent) => {
    // Focus editor on any click in the wrapper, unless clicking a button
    const target = e.target as HTMLElement
    if (!target.closest('button') && editor) {
      editor.commands.focus()
    }
  }

  return (
    <div
      className={cn('border border-input rounded-md overflow-hidden', className)}
      onClick={handleWrapperClick}
      role="button"
      tabIndex={0}
    >
      {!hideButtons && (
        <div className="flex flex-wrap items-center justify-between border-b p-1 px-2 bg-muted/30 gap-2">
          <div className={cn('hidden md:flex items-center gap-1 flex-wrap', disabled && 'pointer-events-none')}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={cn('w-8 h-8', editor.isActive('bold') ? 'bg-muted' : '')}
            >
              <BoldIcon className="w-4 h-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={cn('w-8 h-8', editor.isActive('italic') ? 'bg-muted' : '')}
            >
              <ItalicIcon className="w-4 h-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={cn('w-8 h-8', editor.isActive('underline') ? 'bg-muted' : '')}
            >
              <UnderlineIcon className="w-4 h-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={cn('w-8 h-8', editor.isActive('heading', { level: 1 }) ? 'bg-muted' : '')}
            >
              <Heading1Icon className="w-4 h-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={cn('w-8 h-8', editor.isActive('heading', { level: 2 }) ? 'bg-muted' : '')}
            >
              <Heading2Icon className="w-4 h-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              className={cn('w-8 h-8', editor.isActive('heading', { level: 3 }) ? 'bg-muted' : '')}
            >
              <Heading3Icon className="w-4 h-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
              className={cn('w-8 h-8', editor.isActive('heading', { level: 4 }) ? 'bg-muted' : '')}
            >
              <Heading4Icon className="w-4 h-4" />
            </Button>

            {/* <div className="w-px h-6 bg-border mx-1" /> */}

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleOpenLinkDialog}
              className={cn('w-8 h-8', editor.isActive('link') ? 'bg-muted' : '')}
            >
              <LinkIcon className="w-4 h-4" />
            </Button>

            {editor.isActive('link') && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleRemoveLink}
                className="w-8 h-8 text-red-600 hover:text-red-700"
              >
                <UnlinkIcon className="w-4 h-4" />
              </Button>
            )}

            {/* <div className="w-px h-6 bg-border mx-1" /> */}

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={cn('w-8 h-8', editor.isActive('bulletList') ? 'bg-muted' : '')}
            >
              <ListIcon className="w-4 h-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={cn('w-8 h-8', editor.isActive('orderedList') ? 'bg-muted' : '')}
            >
              <ListOrderedIcon className="w-4 h-4" />
            </Button>
          </div>

          {headerRight && <div className="flex items-center">{headerRight}</div>}
        </div>
      )}

      <EditorContent
        editor={editor}
        className={cn(
          'rich-text-editor min-h-[120px] bg-background',
          disabled && 'pointer-events-none'
        )}
      />

      {/* Floating Bubble Menu for Text Highlighting */}
      {editor && (
        <BubbleMenu
          editor={editor}
          updateDelay={100}
          appendTo={() => document.body}
          options={{
            placement: 'top',
            offset: 10,
          }}
          className="bubble-menu-container flex items-center gap-1 bg-white border border-border rounded-lg shadow-xl p-1.5 z-[9999] pointer-events-auto"
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          {/* Bold */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              editor.chain().focus().toggleBold().run()
            }}
            className={cn(
              'w-7 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors font-bold cursor-pointer',
              editor.isActive('bold') && 'bg-gray-300'
            )}
            title="Vet (Ctrl+B)"
          >
            B
          </button>

          {/* Italic */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              editor.chain().focus().toggleItalic().run()
            }}
            className={cn(
              'w-7 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors italic cursor-pointer',
              editor.isActive('italic') && 'bg-gray-300'
            )}
            title="Cursief (Ctrl+I)"
          >
            I
          </button>

          {/* Underline */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              editor.chain().focus().toggleUnderline().run()
            }}
            className={cn(
              'w-7 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors underline cursor-pointer',
              editor.isActive('underline') && 'bg-gray-300'
            )}
            title="Onderstreept (Ctrl+U)"
          >
            U
          </button>

          <div className="w-px h-5 bg-border mx-0.5" />

          {/* Yellow */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              editor.chain().focus().toggleHighlight({ color: '#fef08a' }).run()
            }}
            className={cn(
              'w-7 h-7 rounded hover:bg-accent flex items-center justify-center transition-colors cursor-pointer',
              editor.isActive('highlight', { color: '#fef08a' }) && 'bg-accent'
            )}
            title="Gele markering"
          >
            <div className="w-5 h-5 rounded bg-yellow-300" />
          </button>

          {/* Green */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              editor.chain().focus().toggleHighlight({ color: '#86efac' }).run()
            }}
            className={cn(
              'w-7 h-7 rounded hover:bg-accent flex items-center justify-center transition-colors cursor-pointer',
              editor.isActive('highlight', { color: '#86efac' }) && 'bg-accent'
            )}
            title="Groene markering"
          >
            <div className="w-5 h-5 rounded bg-green-300" />
          </button>

          {/* Blue */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              editor.chain().focus().toggleHighlight({ color: '#93c5fd' }).run()
            }}
            className={cn(
              'w-7 h-7 rounded hover:bg-accent flex items-center justify-center transition-colors cursor-pointer',
              editor.isActive('highlight', { color: '#93c5fd' }) && 'bg-accent'
            )}
            title="Blauwe markering"
          >
            <div className="w-5 h-5 rounded bg-blue-300" />
          </button>

          {/* Purple */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              editor.chain().focus().toggleHighlight({ color: '#d8b4fe' }).run()
            }}
            className={cn(
              'w-7 h-7 rounded hover:bg-accent flex items-center justify-center transition-colors cursor-pointer',
              editor.isActive('highlight', { color: '#d8b4fe' }) && 'bg-accent'
            )}
            title="Paarse markering"
          >
            <div className="w-5 h-5 rounded bg-purple-300" />
          </button>

          {/* Orange */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              editor.chain().focus().toggleHighlight({ color: '#fdba74' }).run()
            }}
            className={cn(
              'w-7 h-7 rounded hover:bg-accent flex items-center justify-center transition-colors cursor-pointer',
              editor.isActive('highlight', { color: '#fdba74' }) && 'bg-accent'
            )}
            title="Oranje markering"
          >
            <div className="w-5 h-5 rounded bg-orange-300" />
          </button>

          {/* Pink */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              editor.chain().focus().toggleHighlight({ color: '#fda4af' }).run()
            }}
            className={cn(
              'w-7 h-7 rounded hover:bg-accent flex items-center justify-center transition-colors cursor-pointer',
              editor.isActive('highlight', { color: '#fda4af' }) && 'bg-accent'
            )}
            title="Roze markering"
          >
            <div className="w-5 h-5 rounded bg-pink-300" />
          </button>

          <div className="w-px h-5 bg-border mx-0.5" />

          {/* Remove highlight */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              editor.chain().focus().unsetHighlight().run()
            }}
            className="w-7 h-7 rounded hover:bg-accent flex items-center justify-center transition-colors text-xs font-medium cursor-pointer"
            title="Verwijder markering"
          >
            ✕
          </button>
        </BubbleMenu>
      )}

      {/* Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Link toevoegen</DialogTitle>
            <DialogDescription>Voer de URL in voor de hyperlink</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <Input
              type="url"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)}
              onKeyDown={handleKeyDownInLinkDialog}
              autoFocus
            />
          </div>
          <DialogFooter className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowLinkDialog(false)
                setLinkUrl('')
              }}
            >
              Annuleren
            </Button>
            <Button
              type="button"
              variant="outline"
              className="text-red-600 hover:text-red-700"
              onClick={handleRemoveLink}
            >
              Verwijderen
            </Button>
            <Button type="button" onClick={handleAddLink}>
              Toevoegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
